import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

import PublicLayout from "./components/PublicLayout";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Releases from "./pages/Releases";
import About from "./pages/About";
import Book from "./pages/Book";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import PaymentResult from "./pages/PaymentResult";
import Policies from "./pages/Policies";

import PortalLayout from "./portal/PortalLayout";
import PortalDashboard from "./portal/PortalDashboard";
import PortalBookings from "./portal/PortalBookings";
import PortalProjects from "./portal/PortalProjects";
import PortalMessages from "./portal/PortalMessages";
import PortalNotifications from "./portal/PortalNotifications";
import PortalProfile from "./portal/PortalProfile";

import AdminLayout from "./admin/AdminLayout";
import AdminOverview from "./admin/AdminOverview";
import AdminBookings from "./admin/AdminBookings";
import AdminCalendar from "./admin/AdminCalendar";
import AdminClients from "./admin/AdminClients";
import AdminProjects from "./admin/AdminProjects";
import AdminServices from "./admin/AdminServices";
import AdminSms from "./admin/AdminSms";
import AdminPayments from "./admin/AdminPayments";
import AdminReleases from "./admin/AdminReleases";
import AdminReviews from "./admin/AdminReviews";
import AdminMessages from "./admin/AdminMessages";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-loading">
    <div className="flex items-end gap-1 h-8">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="eq-bar w-1.5 h-8 bg-purple-700 rounded-full" style={{ animationDelay: `${i * 0.12}s` }} />
      ))}
    </div>
  </div>
);

function RequireAuth({ children, admin = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <Loader />;
  if (!user) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/portal" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/releases" element={<Releases />} />
        <Route path="/about" element={<About />} />
        <Route path="/book" element={<Book />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/payment/success" element={<PaymentResult mode="success" />} />
        <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />
      </Route>
      <Route path="/portal" element={<RequireAuth><PortalLayout /></RequireAuth>}>
        <Route index element={<PortalDashboard />} />
        <Route path="bookings" element={<PortalBookings />} />
        <Route path="projects" element={<PortalProjects />} />
        <Route path="messages" element={<PortalMessages />} />
        <Route path="notifications" element={<PortalNotifications />} />
        <Route path="profile" element={<PortalProfile />} />
      </Route>
      <Route path="/admin" element={<RequireAuth admin><AdminLayout /></RequireAuth>}>
        <Route index element={<AdminOverview />} />
        <Route path="calendar" element={<AdminCalendar />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="projects" element={<AdminProjects />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="sms" element={<AdminSms />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="releases" element={<AdminReleases />} />
        <Route path="reviews" element={<AdminReviews />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
